import MarkerClusterer from '@googlemaps/markerclustererplus';
import {isMobile} from 'react-device-detect';
import {BehaviorSubject, Observable} from 'rxjs';
import {CLUSTERER_CONFIG, getMapStyles, INIT_BOUNDS, INIT_COORDS, INIT_ZOOM} from '../constants';
import {getCurrentPosition, getInfoTimeMarkers, getLocationHalo, getLocationShapes, getPointerMarker, getRealTimeMarker} from '../utils';
import {ZbeApi} from '../../services/api/zbe/zbe.api';

export class Map {

    private readonly map: google.maps.Map;
    private kmlLayer: google.maps.KmlLayer | null = null;

    private markers: google.maps.Marker[] = [];
    private markerClusterer: MarkerClusterer;
    private locationShapes: google.maps.Polyline[] = [];
    private allZbePolygons: google.maps.Polygon[] = [];
    private freeRoadPolylines: google.maps.Polyline[] = [];
    private readonly zbeApi = new ZbeApi();
    private locationHalo: google.maps.Marker;
    private locationInfo: google.maps.Marker[] = [];
    private pointerMarker: google.maps.Marker;
    private pointerTimeout: any;
    private routes: (google.maps.Polyline | google.maps.Marker)[] = [];
    private realTimeMarkers: google.maps.Marker[] = [];

    private tileLayer: google.maps.ImageMapType | null = null;

    public currentLocation: google.maps.LatLngLiteral | null = null;
    private bounds: BehaviorSubject<google.maps.LatLngBoundsLiteral>;
    public bounds$: Observable<google.maps.LatLngBoundsLiteral>;
    private isPointerActive: boolean = false;
    private isNoParkingActive: boolean = false;
    private pointer: BehaviorSubject<google.maps.LatLngLiteral | null>;
    public pointer$: Observable<google.maps.LatLngLiteral | null>;
    private pointerCoords: BehaviorSubject<google.maps.LatLngLiteral | null>;
    public pointerCoords$: Observable<google.maps.LatLngLiteral | null>;

    constructor(element: HTMLElement, theme: string) {
        this.map = new window.google.maps.Map(element, {
            center: INIT_COORDS,
            zoom: INIT_ZOOM,
            fullscreenControl: false,
            zoomControl: !isMobile,
            mapTypeControl: false,
            styles: getMapStyles(theme),
        });

        this.markerClusterer = new MarkerClusterer(this.map, [], CLUSTERER_CONFIG);
        this.locationHalo = getLocationHalo();
        this.pointerMarker = getPointerMarker();
        this.setInfoTimeMarkers();

        this.bounds = new BehaviorSubject<google.maps.LatLngBoundsLiteral>(INIT_BOUNDS);
        this.bounds$ = this.bounds.asObservable();
        this.pointer = new BehaviorSubject<google.maps.LatLngLiteral | null>(null);
        this.pointer$ = this.pointer.asObservable();
        this.pointerCoords = new BehaviorSubject<google.maps.LatLngLiteral | null>(null);
        this.pointerCoords$ = this.pointerCoords.asObservable();
        this.map.addListener('bounds_changed', this.boundsHandler.bind(this));
        this.map.addListener('mousedown', this.mouseDownHandler.bind(this));
        this.map.addListener('drag', this.cancelTimeoutPointer.bind(this));
        this.map.addListener('mouseup', this.cancelTimeoutPointer.bind(this));
        this.isNoParkingActive = false;
        this.subscribeToNavigationChanges();

        this.initializeCurrentLocation();
    }

    private handleNavigationChange() {
        if (window.location.pathname === '/home') {
            this.setPointer(null);
        }
    }

    private subscribeToNavigationChanges() {
        window.onpopstate = this.handleNavigationChange.bind(this);
    }

    public addTileLayer(urlTemplate: string, options?: google.maps.ImageMapTypeOptions): void {
        const tileLayer = new google.maps.ImageMapType({
            getTileUrl: function(coord, zoom) {
                const x = coord.x;
                const y = coord.y;
                const z = zoom;
                const tileUrl = urlTemplate
                    .replace('{x}', x.toString())
                    .replace('{y}', y.toString())
                    .replace('{z}', z.toString());
                return tileUrl;
            },
            tileSize: new google.maps.Size(256, 256),
            ...options,
        });
        this.tileLayer = tileLayer;
        this.map.overlayMapTypes.push(tileLayer);
    };
    public removeTileLayer(): void {
        if (this.tileLayer) {
            const overlayMapTypes = this.map.overlayMapTypes;
            const index = overlayMapTypes.getArray().indexOf(this.tileLayer);
            if (index > -1) {
                overlayMapTypes.removeAt(index);
                this.tileLayer = null;
            }
        }
    }

    private async initializeCurrentLocation() {
        try {
            this.currentLocation = await getCurrentPosition();
            this.drawCurrentLocationShapes(this.pointer.value || this.currentLocation);
            this.setPointer(null);
            this.map.panTo(this.currentLocation);
            this.map.setZoom(16);
        } catch (error) {
            console.error('Error getting current position:', error);
        }
    }
    private drawCurrentLocationShapes(location: google.maps.LatLngLiteral): void {
        this.clearLocationShapes();
        this.locationShapes = getLocationShapes(new google.maps.LatLng(location));
        this.locationShapes.forEach((shape) => shape.setMap(this.map));
    }
    private clearLocationShapes(): void {
        this.locationShapes.forEach((shape) => shape.setMap(null));
    }

    public drawMarkers(markers: google.maps.Marker[]) {
        if (this.routes.length) {
            this.markerClusterer.clearMarkers();
        } else {
            this.markers = markers;
            const currentMarkers = this.markerClusterer.getMarkers();
            const toRemove = currentMarkers.filter((marker) => !markers.some((m) => m.get('id') === marker.get('id')));
            const toAdd = markers.filter((marker) => !currentMarkers.some((m) => m.get('id') === marker.get('id')));
            this.markerClusterer.removeMarkers(toRemove);
            this.markerClusterer.addMarkers(toAdd);
        }
        this.drawCurrentLocation();
    }

    public async drawCurrentLocation() {
        this.clearLocationShapes(); // Primero limpia todos los circulos asi empezamos de 0
        // Hacemos una lógica para dibujar los circulos, si hay un closemarker, dibujamos los circulos ahí, si no, los dibujamos en la ubicación actual
        if (!this.routes.length) {
            if (this.pointer.value) {
                this.locationShapes = getLocationShapes(new google.maps.LatLng(this.pointer.value));
                // this.locationShapes = getLocationShapes(new google.maps.LatLng(this.currentLocation || INIT_COORDS)); // Applike
            } else if (this.currentLocation) {
                this.locationShapes = getLocationShapes(new google.maps.LatLng(this.currentLocation));
            }
            this.locationShapes.forEach((shape) => shape.setMap(this.map));
            this.locationHalo.setMap(this.map);
            this.locationHalo.setPosition(this.currentLocation);
        } else {
            // Esto lo he tenido que añadir por que al volver, se quedaban los círculos en la ubicación anterior
            this.locationHalo.setMap(this.map);
        }
    }

    public async drawZbeInMap() {
        try {
            const zbeData = await this.zbeApi.getzbe();
            const otherZbeData = await this.zbeApi.getOtherzbe();
            // const freeRoadsZbeData = await this.zbeApi.getFreeRoadszbe();

            const allZbes = [
                ...zbeData.features,
                ...otherZbeData.features,
            ];

            allZbes.forEach((zbe: any) => {
                const coordinates = zbe.geometry.coordinates[0];
                this.createZbe(coordinates);
            });

            /* freeRoadsZbeData.features.forEach((road: any) => {
                const coordinates = road.geometry.coordinates;
                this.createFreeRoadZbe(coordinates);
            }); */

        } catch (error) {
            console.error('Error fetching ZBE data:', error);
        }
    }

    public createZbe(Zbe: any) {
        const zbeCoords = Zbe.map((coord: any) => ({
            lat: coord[1],
            lng: coord[0],
        }));

        const zbeMap = new google.maps.Polygon({
            paths: zbeCoords,
            strokeColor: '#eb3462',
            strokeOpacity: 1,
            strokeWeight: 2,
            fillColor: '#eb3462',
            fillOpacity: 0.1,
            clickable: false,
        });

        zbeMap.setMap(this.map);
        this.allZbePolygons.push(zbeMap);
    }

    public removeZbeInMap() {
        this.allZbePolygons.forEach((polygon) => {
            polygon.setMap(null);
        });
        this.allZbePolygons = [];

        this.freeRoadPolylines.forEach((polyline) => {
            polyline.setMap(null);
        });
        this.freeRoadPolylines = [];
    }

    public createFreeRoadZbe(coordinates: any) {
        const roadCoords = coordinates.map((coord: any) => ({
            lat: coord[1],
            lng: coord[0],
        }));

        const roadLine = new google.maps.Polyline({
            path: roadCoords,
            strokeColor: '#eb3462',
            strokeOpacity: 1,
            strokeWeight: 2,
            clickable: false,
        });

        roadLine.setMap(this.map);
        this.freeRoadPolylines.push(roadLine);
    }
    public loadKmlLayer(kmlUrl: string): void {
        if (this.kmlLayer) {
            this.kmlLayer.setMap(null);
        }
        const currentCenter:any = this.map.getCenter();
        const currentZoom:any = this.map.getZoom();
        this.kmlLayer = new google.maps.KmlLayer({
            url: kmlUrl,
            preserveViewport: true,
        });
        this.kmlLayer?.setMap(this.map);
        this.isNoParkingActive = true;
        google.maps.event.addListenerOnce(this.kmlLayer, 'defaultviewport_changed', () => {
            this.map.setCenter(currentCenter);
            this.map.setZoom(currentZoom);
            setTimeout(() => {
                const svgs = document.querySelectorAll('#map > div > div.gm-style > div:nth-child(1) > div:nth-child(1) > div:nth-child(2)');
                svgs.forEach((el: any) => {
                    el.style.zIndex = '2000';
                });
            }, 2000);
        });
    }

    public removeKmlLayer(): void {
        if (this.kmlLayer) {
            this.kmlLayer.setMap(null);
            this.isNoParkingActive = false;
        }
    }

    public isKmlLayerActive() {
        return this.isNoParkingActive;
    }

    public setZoom(zoom: number): void {
        this.map.setZoom(zoom);
    }

    public drawTrip(polyline: google.maps.Polyline, stops: google.maps.Marker[]) {

        this.markerClusterer.clearMarkers();
        this.clearLocationShapes();

        this.routes = [...this.routes, polyline, ...stops];

        polyline.setMap(this.map);
        stops.forEach((stop) => stop.setMap(this.map));
    }

    public drawTripMarker(realtimesinfo: any[]) {
        this.clearRealTimeMarkers();

        if (realtimesinfo && Array.isArray(realtimesinfo)) {
            realtimesinfo.forEach((realTime) => {
                const {latitude, longitude} = realTime;

                const latToUse = latitude !== null ? parseFloat(latitude) : latitude;
                const lngToUse = longitude !== null ? parseFloat(longitude) : longitude;

                const realTimeMarker = getRealTimeMarker();
                realTimeMarker.setPosition({
                    lat: latToUse,
                    lng: lngToUse,
                });

                realTimeMarker.setMap(this.map);
                this.realTimeMarkers.push(realTimeMarker);
            });
        }
    }

    public clearTrips() {
        this.routes.forEach((polyline) => {
            polyline.setMap(null);
        });
        this.routes = [];

        this.markerClusterer.addMarkers(this.markers);
    }

    public clearRealTimeMarkers() {
        this.realTimeMarkers.forEach((marker) => {
            marker.setMap(null);
        });
        this.realTimeMarkers = [];
    }

    public async setCurrentLocationMarker(): Promise<void> {
        try {
            this.currentLocation = await getCurrentPosition();
            this.setPointer(null);
            this.map.panTo(this.currentLocation);
            this.map.setZoom(16);
        } catch (e) {
            throw e;
        }
    }

    public zoomPosition(position: google.maps.LatLngLiteral): void {

        if (!this.map.getBounds()?.contains(position) || (this.map.getZoom() || 0) < 16) {
            this.map.panTo(position);
            this.map.setZoom(16);
        }

    }

    public fitBounds(bounds: google.maps.LatLngBounds): void {
        this.map.fitBounds(bounds);
    }
    public panTo(position: google.maps.LatLngLiteral): void {
        this.map.panTo(position);
    }
    private async setInfoTimeMarkers(): Promise<void> {
        this.locationInfo = await getInfoTimeMarkers();
    }

    public setPointer(position: google.maps.LatLngLiteral | null): void {

        this.pointerMarker.setMap(position ? this.map : null);
        this.pointerMarker.setPosition(position);
        if (position) this.zoomPosition(position);

        this.isPointerActive = !!position;
        this.pointer.next(position);

        // Si modifico esta función, los polylines NO se dibujarán en el closeStops
        if (!position) {
            this.drawCurrentLocationShapes(this.currentLocation || INIT_COORDS); // Redibuja los polylines en la ubicación actual o inicial
        }
    }

    private boundsHandler(): void {
        const bounds = this.map.getBounds();
        if (bounds) {
            this.bounds.next(bounds.toJSON());
        }
    }

    private mouseDownHandler(event: google.maps.MapMouseEvent & PointerEvent): void {
        // Verificar si es un evento válido en desktop o mobile
        const isDesktopClick = !isMobile && event.domEvent instanceof MouseEvent && event.domEvent.button === 0;
        const isSingleTouch = isMobile && event.domEvent instanceof TouchEvent && event.domEvent.touches.length === 1;

        if (isDesktopClick || isSingleTouch) {
            this.pointerTimeout = setTimeout(() => {
                this.pointerCoords.next(event.latLng?.toJSON() || null);
            }, 500);
        }
    }

    private cancelTimeoutPointer(): void {
        clearTimeout(this.pointerTimeout);
    }
}
